import { useCallback, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '../../config/AppConfigContext';

const joinUrl = (baseUrl: string, path: string) => {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

export interface StreamMessageParams {
  userId: string;
  companyId: string;
  message: string;
  conversationId?: string | null;
}

export interface StreamMessageHandlers {
  onChunk: (text: string) => void;
  onDone: (conversationId: string) => void;
  onError: (error: { message: string; status?: number }) => void;
}

interface SseEvent {
  event: string;
  data: string;
}

/**
 * Parse a buffer of raw SSE bytes into discrete events.
 * Returns the parsed events plus the trailing partial event still in the buffer.
 */
const drainSseEvents = (buffer: string): { events: SseEvent[]; remainder: string } => {
  const events: SseEvent[] = [];
  let working = buffer;
  let separatorIndex = working.indexOf('\n\n');
  while (separatorIndex !== -1) {
    const block = working.slice(0, separatorIndex);
    working = working.slice(separatorIndex + 2);
    let eventName = 'message';
    let dataLine = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLine += line.slice('data:'.length).trim();
      }
    }
    if (dataLine) events.push({ event: eventName, data: dataLine });
    separatorIndex = working.indexOf('\n\n');
  }
  return { events, remainder: working };
};

/**
 * Hook that posts to the partner SSE chat endpoint and forwards parsed events.
 *
 * The mutation-style API mirrors useSendMessageMutation but is exposed as a
 * plain function because react-query mutations don't natively model streamed
 * responses.
 */
export const useStreamMessage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();
  const queryClient = useQueryClient();
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const streamMessage = useCallback(
    async (params: StreamMessageParams, handlers: StreamMessageHandlers) => {
      if (!allyApi) {
        handlers.onError({ message: 'Missing allyApi configuration' });
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);

      try {
        const token = auth0Audience
          ? await getAccessTokenSilently({ authorizationParams: { audience: auth0Audience } })
          : await getAccessTokenSilently();

        const body: Record<string, string> = {
          userId: params.userId,
          companyId: params.companyId,
          message: params.message,
        };
        if (params.conversationId) body.conversationId = params.conversationId;

        const response = await fetch(joinUrl(allyApi, 'api/v1/partner/chat/stream'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          handlers.onError({
            message: (errorData as { error?: string }).error || `HTTP ${response.status}`,
            status: response.status,
          });
          return;
        }

        if (!response.body) {
          handlers.onError({ message: 'Streaming response has no body' });
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let conversationId: string | null = null;

         
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const { events, remainder } = drainSseEvents(buffer);
          buffer = remainder;

          for (const evt of events) {
            let parsed: unknown;
            try {
              parsed = JSON.parse(evt.data);
            } catch {
              continue;
            }

            if (evt.event === 'error') {
              const err = parsed as { error?: string; status?: number };
              handlers.onError({
                message: err.error || 'Stream error',
                status: err.status,
              });
              continue;
            }

            const payload = parsed as { chunk?: string; conversationId?: string; done?: boolean };
            if (typeof payload.chunk === 'string') {
              handlers.onChunk(payload.chunk);
            }
            if (payload.done && typeof payload.conversationId === 'string') {
              conversationId = payload.conversationId;
            }
          }
        }

        if (conversationId) {
          handlers.onDone(conversationId);
          void queryClient.invalidateQueries({
            queryKey: ['partner', 'users', params.userId, 'conversations'],
          });
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        const message = err instanceof Error ? err.message : 'Streaming failed';
        handlers.onError({ message });
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setIsStreaming(false);
      }
    },
    [allyApi, auth0Audience, getAccessTokenSilently, queryClient]
  );

  return { streamMessage, cancel, isStreaming };
};
